/**
 * O ícone bloqueado com o motivo, para quem já tem o veredito na mão — issue #408.
 *
 * `ActionIconButton` faz a pergunta e desenha o botão, e serve ao controle que age na própria
 * tela. Falta o outro lado: o ícone que **leva a uma tela**, cujo veredito vem de `useControle`
 * e não pode ser perguntado aqui dentro — a rota de destino é da tela, não do componente. Sem
 * este componente, cada tabela reescreve o mesmo `Tooltip` + `<span>` + `disabled`, e basta uma
 * cópia esquecer o `<span>` para o tooltip do estado bloqueado nunca abrir.
 *
 * O `<span>` está sempre presente, e não só quando bloqueia: botão desabilitado não emite
 * evento de mouse, e sem o invólucro o tooltip — a única explicação que a pessoa recebe —
 * nunca abriria. O `aria-label` é o tooltip inteiro porque em ícone ele **é** o nome acessível:
 * o rótulo vem na frente do motivo, e quem navega por leitor de tela continua sabendo qual
 * ação era.
 */

import React from 'react';
import { IconButton, Tooltip, type IconButtonProps } from '@mui/material';

import type { ControleDeAcesso } from '../../authorization/useVeredito';

export interface ControleIconButtonProps extends Omit<IconButtonProps, 'aria-label'> {
  /** O que `useControle`/`useAcao` respondeu: desabilitado e o que dizer. */
  controle: ControleDeAcesso;
}

export const ControleIconButton: React.FC<ControleIconButtonProps> = ({
  controle,
  disabled,
  onClick,
  children,
  ...props
}) => (
  <Tooltip title={controle.tooltip}>
    <span>
      <IconButton
        {...props}
        aria-label={controle.tooltip}
        disabled={controle.disabled || disabled}
        onClick={controle.disabled ? undefined : onClick}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

export default ControleIconButton;
